import React from 'react';

import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import DatePicker from 'react-datepicker';
import cn from 'classnames';

import { Text, Button } from 'modules/common';
import { useOnClickOutside } from 'modules/common/hooks';

import CustomHeader from './CustomHeader';
import * as c from './constants';

function FormDateRangePicker({
  value,
  format,
  name,
  hasAllTime,
  labelClassName,
  label,
  required,
  isOptional,
  containerClassName,
  placeholder,
  error,
  helperText,
  errorClassName,
  disabled,
  isMobile,
  onSetFieldValue,
  onChange,
}) {
  const [isShow, setIfShow] = React.useState(false);
  const [animate, setAnimate] = React.useState(false);
  const showRef = React.useRef();

  useOnClickOutside(showRef, setIfShow);

  const dateRangeOpts = React.useMemo(() => {
    if (hasAllTime) {
      return [
        ...c.DATE_RANGE_OPTS,
        {
          label: 'All Time',
          value: 'all_time',
          data: {},
        },
      ];
    }
    return c.DATE_RANGE_OPTS;
  }, [hasAllTime]);

  const [date, setDate] = React.useState({
    from: value?.from ? dayjs(value?.from).format(format) : value?.from,
    to: value?.to ? dayjs(value?.to).format(format) : value?.to,
    range: value?.range,
  });

  const renderPlaceholder = React.useMemo(() => {
    const { from, to } = date;
    if (from && to) {
      const f = dayjs(from).format(format);
      const t = dayjs(to).format(format);

      const renderDate = f === t ? f : `${f} - ${t}`;

      return renderDate;
    }
    return placeholder;
  }, [date, format, placeholder]);

  const handleOnChangeDate = ([start, end]) => {
    setDate((prev) => ({
      ...prev,
      from: start,
      to: end,
    }));
  };

  const handleDateSelect = () => {
    setDate((prev) => ({
      ...prev,
      range: dateRangeOpts[0],
    }));
  };

  const handleShowDate = () => {
    if (!isShow) {
      setIfShow(true);
      setTimeout(() => {
        setAnimate(true);
      }, 100);
      return;
    }

    setDate({
      from: value?.from,
      to: value?.to,
      range: value?.range,
    });

    setAnimate(false);
    setTimeout(() => {
      setIfShow(false);
    }, 100);
  };

  const handleOnChangeSelect = (v) => {
    setDate((prev) => ({
      ...prev,
      range: v,
      from: v?.data?.from,
      to: v?.data?.to,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const renderTo = () => {
      if (date?.from && !date?.to) {
        setDate((prev) => ({
          ...prev,
          to: dayjs(date?.from).format(format),
        }));

        return dayjs(date?.from).format(format);
      }

      return date?.to ? dayjs(date?.to).format(format) : '';
    };

    const args = {
      ...date,
      from: date?.from ? dayjs(date?.from).format(format) : '',
      to: renderTo(),
    };

    if (typeof onSetFieldValue === 'function') {
      onSetFieldValue(name, args);
      return;
    }

    if (typeof onChange === 'function')
      onChange((prevState) => ({
        ...prevState,
        [name]: args,
      }));

    setAnimate(false);
    setTimeout(() => {
      setIfShow(false);
    }, 100);
  };

  const handleOnClear = () => {
    setDate({
      from: '',
      to: '',
      range: {
        label: 'Custom',
        value: '',
        data: '',
      },
    });
  };

  return (
    <div className="w-full relative" ref={showRef}>
      {label && (
        <label
          className={cn('ds-form-label flex items-center', labelClassName)}
          htmlFor={name}
        >
          <div className="flex-1">
            {label}{' '}
            {required ? (
              <span className="text-utility-danger-base">*</span>
            ) : (
              ''
            )}
          </div>
          {isOptional && <div className="font-normal">Optional</div>}
        </label>
      )}

      <button
        className={cn(
          'transition-all duration-300 ease-in-out flex space-x-2 disabled:bg-gray-100 disabled:opacity-100',
          containerClassName,
          {
            'form-input': !error,
            'form-input-error': error,
          }
        )}
        onClick={handleShowDate}
        type="button"
        id={name}
      >
        <span
          className={cn({
            'form-icon': !disabled,
            'form-icon-disabled': disabled,
          })}
        >
          <c.CalendarIcon className="h-4 w-4" />
        </span>

        <div
          className={cn('flex-1 text-left', {
            'text-placeholder': !date?.from && !date?.to,
          })}
        >
          {renderPlaceholder}
        </div>
      </button>

      {(Boolean(error) || Boolean(helperText)) && (
        <Text
          type="caption"
          className={cn(
            'mt-ds-6',
            {
              'ds-form-helper': !error,
              'ds-form-helper-error': error,
              'ds-form-helper-disabled': disabled,
            },
            errorClassName
          )}
        >
          <span>{error || helperText || ''}</span>
          <span className="form-error-badge" />
        </Text>
      )}

      {isShow && (
        <div
          className={cn(
            'flex flex-col rounded border divide-y shadow-tooltip transition transform ease-out duration-100 origin-top z-40 absolute mt-1 to bg-white h-auto',
            {
              'w-[297px] md:w-[712px]': !isMobile,
              'w-[297px]': isMobile,
              'opacity-0': !animate,
              'opacity-100': animate,
            }
          )}
        >
          <div
            className={cn('flex flex-col md:flex-row md:divide-x', {
              '!flex-col !divide-x-0': isMobile,
            })}
          >
            <div
              className={cn(
                'space-y-ds-12 p-ds-20 pb-0 md:p-ds-20 md:w-[135px]',
                {
                  '!w-auto !pb-0': isMobile,
                }
              )}
            >
              <Text type="caption" weight="semibold">
                Time Presets
              </Text>

              <div
                className={cn(
                  'flex flex-row flex-wrap md:flex-col gap-0 md:gap-ds-2',
                  {
                    '!flex-row !gap-0': isMobile,
                  }
                )}
              >
                {dateRangeOpts?.map((item, key) => (
                  <button
                    type="button"
                    className={cn('p-[3px] text-left ml-0 md:ml-[-3px]', {
                      '!ml-0': isMobile,
                    })}
                    onClick={() => handleOnChangeSelect(item)}
                  >
                    <Text
                      // eslint-disable-next-line react/no-array-index-key
                      key={key}
                      type="caption"
                      color={
                        item?.value === date?.range?.value
                          ? 'brand-primary-base'
                          : 'muted'
                      }
                      weight={
                        item?.value === date?.range?.value ? 'bold' : 'normal'
                      }
                    >
                      {item?.label}
                    </Text>
                  </button>
                ))}
              </div>
            </div>

            <div
              className={cn('p-ds-20 pt-ds-12 md:p-ds-20', {
                '!pt-ds-12': isMobile,
              })}
            >
              <div className={cn({ 'md:hidden': !isMobile })}>
                <DatePicker
                  inline
                  selected={date?.from ? new Date(dayjs(date?.from)) : ''}
                  startDate={date?.from ? new Date(dayjs(date?.from)) : ''}
                  endDate={date?.to ? new Date(dayjs(date?.to)) : ''}
                  onChange={handleOnChangeDate}
                  selectsRange
                  shouldCloseOnSelect={false}
                  focusSelectedMonth={false}
                  monthsShown={1}
                  name={name}
                  id={name}
                  renderCustomHeader={(props) => (
                    <CustomHeader
                      props={{
                        ...props,
                        monthsShown: 1,
                      }}
                    />
                  )}
                  calendarClassName="customize-datepicker-theme"
                  disabledKeyboardNavigation
                  formatWeekDay={(nameOfDay) => nameOfDay.substr(0, 3)}
                  onSelect={handleDateSelect}
                />
              </div>

              <div
                className={cn({
                  'hidden md:block': !isMobile,
                  hidden: isMobile,
                })}
              >
                <DatePicker
                  inline
                  selected={date?.from ? new Date(dayjs(date?.from)) : ''}
                  startDate={date?.from ? new Date(dayjs(date?.from)) : ''}
                  endDate={date?.to ? new Date(dayjs(date?.to)) : ''}
                  onChange={handleOnChangeDate}
                  selectsRange
                  shouldCloseOnSelect={false}
                  focusSelectedMonth={false}
                  monthsShown={2}
                  name={name}
                  id={name}
                  renderCustomHeader={(props) => (
                    <CustomHeader
                      props={{
                        ...props,
                        monthsShown: 2,
                      }}
                    />
                  )}
                  calendarClassName="customize-datepicker-theme"
                  disabledKeyboardNavigation
                  formatWeekDay={(nameOfDay) => nameOfDay.substr(0, 3)}
                  onSelect={handleDateSelect}
                />
              </div>
            </div>
          </div>

          <div className={cn('flex p-ds-12')}>
            <div className="flex gap-ds-8 ml-auto">
              <Button
                label="Clear Selection"
                size="sm"
                onClick={handleOnClear}
              />
              <Button
                label="Apply"
                size="sm"
                type="primary"
                onClick={handleSubmit}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FormDateRangePicker;

FormDateRangePicker.defaultProps = {
  value: {
    from: '',
    to: '',
    range: {
      label: 'Custom',
      value: '',
      data: '',
    },
  },
  format: 'MMM DD, YYYY',
  name: '',
  hasAllTime: false,
  labelClassName: '',
  required: false,
  isOptional: false,
  label: 'Date Range',
  containerClassName: '',
  placeholder: 'Select date range',
  error: false,
  helperText: '',
  errorClassName: '',
  disabled: false,
  isMobile: false,
  onChange: false,
  onSetFieldValue: false,
};

FormDateRangePicker.propTypes = {
  value: PropTypes.instanceOf(Object),
  format: PropTypes.string,
  name: PropTypes.string,
  hasAllTime: PropTypes.bool,
  isMobile: PropTypes.bool,
  labelClassName: PropTypes.string,
  required: PropTypes.bool,
  isOptional: PropTypes.bool,
  label: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.string,
    PropTypes.element,
  ]),
  containerClassName: PropTypes.string,
  placeholder: PropTypes.string,
  error: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.element,
    PropTypes.instanceOf(Object),
    PropTypes.bool,
  ]),
  helperText: PropTypes.string,
  errorClassName: PropTypes.string,
  disabled: PropTypes.bool,
  onChange: PropTypes.oneOfType([
    PropTypes.instanceOf(Function),
    PropTypes.bool,
  ]),
  onSetFieldValue: PropTypes.oneOfType([
    PropTypes.instanceOf(Function),
    PropTypes.bool,
  ]),
};
